import React, {useEffect, useMemo, useState} from "react";
import moment from "moment";
import styled from "styled-components";
import {Tag, Popover, Modal, Select, Tooltip} from "antd";
import {
    ClockCircleOutlined,
    UserSwitchOutlined,
    DeleteOutlined,
    EditOutlined,
    SyncOutlined,
    CloseOutlined
} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable/ResponsiveDataTable";
import {ConditionalTooltip} from "components/_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {IconActionButton} from "./IconActionButton";
import {CourierStatusUpdateModal} from "components/_defaultComponents/CourierStatusUpdateModal/CourierStatusUpdateModal";
import {
    TimeChangeModal,
    CourierChangeModal
} from "components/shiftManagement/Components/PoolShiftChangeModals";
import ShiftUpdateModal from "components/shiftManagement/Components/ShiftUpdateModal/ShiftUpdateModal";
import ShiftCourierUpdateModal from "components/shiftManagement/Components/ShiftCourierUpdateModal/ShiftCourierUpdateModal";
import {allowChangeStatuses} from "constants/constants";
import {buildShiftRows} from "../utils/shiftGrouping";
import {TableFillArea} from "../LiveOperationPage.styles";

const cancellationReasons = [
    {value: "kaza", label: "Kaza"},
    {value: "uyumsuzluk", label: "Uyumsuzluk"},
    {value: "hatali_vardiya", label: "Hatalı Vardiya"},
    {value: "saglik", label: "Sağlık"},
    {value: "erken_giris", label: "Erken Giriş"}
];

// Durum filtresinin seçenekleri artık ekrandaki vardiyaların fiili durumlarından türetiliyor
// (LiveOperationPage), ama sıralama burada sabit kalıyor: akışın mantıksal sırası (Beklemede →
// ... → Tamamlandı) korunsun diye, verideki durumlar alfabetik değil bu sıraya göre listelenir.
export const SHIFT_STATUS_ORDER = [
    "Beklemede",
    "Henüz Giriş Yapılmadı",
    "Vardiyada - Müsait",
    "Vardiyada - Siparişte",
    "Vardiyada - Meşgul",
    "Vardiyada - Mola",
    "Giriş Yapılmadan Tamamlandı",
    "Tamamlandı"
];

const STATUS_TAG_STYLE = {
    Beklemede: {background: "#DBEAFE", color: "#1e3a8a", border: "#93c5fd"},
    "Henüz Giriş Yapılmadı": {background: "#D85E5A", color: "#fff", border: "#D85E5A"},
    "Vardiyada - Müsait": {background: "#F5FEED", color: "#237804", border: "#b7eb8f"},
    "Vardiyada - Siparişte": {background: "#DED2E9", color: "#531dab", border: "#d3adf7"},
    "Vardiyada - Meşgul": {background: "#fff1f0", color: "#a8071a", border: "#ffa39e"},
    "Vardiyada - Mola": {background: "#fff7e6", color: "#ad6800", border: "#ffd591"},
    "Giriş Yapılmadan Tamamlandı": {
        background: "#EBF0EB",
        color: "rgba(0, 0, 0, 0.65)",
        border: "#cdd6cd"
    },
    Tamamlandı: {background: "#D4FFA9", color: "#237804", border: "#b0e070"}
};

// Aktif Siparişler tablosundaki durum etiketleriyle (deliveryStateTag/orderTagStyle) aynı
// görünüm: kenarlıklı, 8px radius, iki satıra sarabilen sabit genişlik — "Giriş Yapılmadan
// Tamamlandı" gibi uzun durumlar kesilmeden okunabilsin diye.
const statusTag = status => {
    const style = STATUS_TAG_STYLE[status] || STATUS_TAG_STYLE.Beklemede;
    return (
        <Tag
            style={{
                background: style.background,
                color: style.color,
                borderColor: style.border,
                height: "auto",
                minHeight: 24,
                width: 150,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: "13px",
                padding: "4px 8px",
                overflow: "hidden",
                whiteSpace: "normal",
                textOverflow: "ellipsis"
            }}
        >
            <Shortener text={status} length={SHIFT_STATUS_SHORTENER_LENGTH} />
        </Tag>
    );
};

const NAME_SHORTENER_LENGTH = 14;
const SHIFT_STATUS_SHORTENER_LENGTH = 30;
const shortenedCell = value => <Shortener text={value} length={NAME_SHORTENER_LENGTH} />;

const CourierNameLink = styled.a`
    color: inherit;

    &:hover {
        color: #1890ff;
    }
`;

// İşlemler hücresi. Butonlar arasındaki boşluk IconActionButton'ın kendi `margin-right`i yerine
// buradaki `gap` ile veriliyor: antd Tooltip PASİF bir butonu <span> ile sarmalıyor (disabled
// eleman pointer olayı üretmediği için), o anda buton kendi span'inin tek çocuğu oluyor ve
// IconActionButton'daki `&:last-child { margin-right: 0 }` kuralı eşleşip boşluğu siliyordu —
// tamamlanmış vardiyalarda iki ikon bitişik görünüyordu.
const ActionCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;

    > * {
        margin-right: 0;
    }
`;

const courierNameCell = (value, row) =>
    row.courierId ? (
        <CourierNameLink
            href={`/couriers/${row.courierId}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            <Shortener text={value} length={NAME_SHORTENER_LENGTH} />
        </CourierNameLink>
    ) : (
        shortenedCell(value)
    );

const GROUP_TITLE_SHORTENER_LENGTH = 40;

// Grup başlığı: proje gruplarında "Firma / Şube" iki satır, havuz gruplarında tek satır havuz
// adı — eski Proje kolonunun taşıdığı bilgi artık burada. Aktif Siparişler tablosundaki
// GroupCell ile aynı desen (Firma - Şube kolonunda colSpan ile tam genişlik).
const ShiftGroupCell = ({row}) => (
    <span>
        <Shortener
            text={row.title}
            length={GROUP_TITLE_SHORTENER_LENGTH}
            render={shortText => <strong>{shortText}</strong>}
        />
        {row.subTitle && (
            <Shortener
                text={row.subTitle}
                length={GROUP_TITLE_SHORTENER_LENGTH}
                render={shortText => (
                    <span style={{fontSize: 12, color: "rgba(0, 0, 0, 0.45)", marginLeft: 6}}>
                        {shortText}
                    </span>
                )}
            />
        )}{" "}
        ({row.count})
    </span>
);

// Gece yarısını aşan vardiyalar iki günün listesinde birden çıkar; saatin hangi güne ait
// olduğunu göstermezsek 24 gecesi ve 25 gecesi başlayan iki vardiya aynı "22:00 - 03:00"
// metniyle ayırt edilemez hâle gelir. Kolonu genişletmemek için gün farkı üst simge olarak
// basılır (⁻¹ = önceki gün), tam tarih tooltip'te okunur.
const dayOffsetMark = offset => {
    if (!offset) return null;
    return <sup style={{color: "rgba(0, 0, 0, 0.45)"}}>{offset > 0 ? `+${offset}` : offset}</sup>;
};

// Aksiyonların ne zaman açık olacağı eski Vardiya Yönetimi ekranlarından birebir alınır: dedike
// satırlarda ShiftTable, havuz satırlarda PoolShiftTable kuralları geçerli. İki taraf bilerek
// ayrı tutuldu, çünkü eski ekranlarda da ayrıydı:
//   - Dedike: kurye değişimi vardiya BAŞLAYINCA kapanır, saat değişimi vardiya bitene kadar
//     açıktır, silme işleminin ise zaman kısıtı yoktur (geçmiş vardiya da silinebilir).
//   - Havuz: saat/kurye/silme üçü de vardiya BİTENE kadar açıktır. Backend
//     cancelCourierPoolShift zaten yalnız gelecekteki slotları iptal ettiği için silmenin
//     kapanması BE ile hizalı.
//
// Kapanan aksiyon (eski dedike ekranındaki gibi) gizlenmez, pasif gösterilir: operasyoncu
// aksiyonun var olduğunu ama şu an alınamadığını görsün. Tek istisna silme — o rol bazlı
// gizleniyor (isAdmin), çünkü yetkisi olmayan kullanıcıya aksiyonun varlığını göstermenin
// anlamı yok.
//
// `disabled` yalnız görsel bir bayrak değil: butonların onClick'leri de aynı bayrakla
// korunuyor, böylece DOM'dan disabled niteliği düşürülerek tıklama tetiklense bile işlem
// çalışmıyor.
const shiftActionAvailability = (row, isAdmin, canUpdateShift) => {
    const now = moment();

    if (row.shiftType === "dedicated") {
        // ShiftTable'daki `item?.date &&` koşulu: tarihi olmayan (snapshot) kayıtlarda vardiya
        // düzenleme aksiyonları açılmazdı.
        const hasDate = Boolean(row.raw?.date);
        const shiftStart = moment(row.raw.shiftStartTime);
        const shiftEnd = moment(row.raw.shiftEndTime);
        // 3 dakikalık pay: vardiyanın başlamasına bu kadar kalmışsa kurye artık değiştirilemez.
        const notStarted = shiftStart
            .clone()
            .subtract(3, "minutes")
            .isAfter(now);

        return {
            changeTime: {
                visible: canUpdateShift,
                disabled: !hasDate || shiftEnd.isBefore(now)
            },
            changeCourier: {visible: canUpdateShift, disabled: !hasDate || !notStarted},
            remove: {visible: isAdmin, disabled: false}
        };
    }

    // Havuz vardiyasında saatler "HH:mm" metni; gece yarısını aşan aralık bir gün ileri taşınır
    // (PoolShiftTable ile birebir aynı hesap).
    const shiftStart = moment(`${row.raw.date} ${row.raw.plannedStartTime}`, "YYYY-MM-DD HH:mm");
    const shiftEnd = moment(`${row.raw.date} ${row.raw.plannedEndTime}`, "YYYY-MM-DD HH:mm");
    if (shiftEnd.isBefore(shiftStart)) shiftEnd.add(1, "day");
    const ended = shiftEnd.isBefore(now);

    return {
        changeTime: {visible: canUpdateShift, disabled: ended},
        changeCourier: {visible: canUpdateShift, disabled: ended},
        remove: {visible: isAdmin, disabled: ended}
    };
};

const rawStatusFromLabel = status => {
    if (status === "Vardiyada - Müsait") return "available";
    if (status === "Vardiyada - Meşgul") return "unavailable";
    if (status === "Vardiyada - Mola") return "onBreak";
    if (status === "Vardiyada - Siparişte") return "busy";
    return null;
};

// Vardiyanın bittiğini gösteren durumlar: kurye artık vardiyada olmadığı için durumunu bu
// tablodan değiştirmenin anlamı yok.
const COMPLETED_SHIFT_STATUSES = new Set(["Tamamlandı", "Giriş Yapılmadan Tamamlandı"]);

// Kurye durum değişikliği, kurye detay ekranındaki desenle (courierDetail/components/HistoryTab)
// aynı şekilde yetkilendirilir:
//   1. Rol yoksa (`updateCourierStatus`) buton hiç render edilmez.
//   2. Kuryenin anlık durumu `allowChangeStatuses` içinde değilse buton pasif kalır — siparişi
//      olan kuryenin durumu güncellenemez.
//   3. Pasif olma sebebi tooltip'te yazar; kullanıcı butonun neden çalışmadığını görür.
// Bu ekrana özel ek kural, satırın vardiya durumu: tamamlanmış vardiyalarda da pasif.
const courierStatusChangeAvailability = (row, canUpdateStatus) => {
    if (!canUpdateStatus) return {visible: false, disabled: true, tooltip: ""};
    if (!row?.courierId) {
        return {visible: true, disabled: true, tooltip: "Vardiyaya atanmış kurye yok."};
    }
    if (COMPLETED_SHIFT_STATUSES.has(row.status)) {
        return {
            visible: true,
            disabled: true,
            tooltip: "Vardiya tamamlandığı için kuryenin durumu değiştirilemez."
        };
    }
    const courierStatus = rawStatusFromLabel(row.status);
    if (courierStatus && !allowChangeStatuses.has(courierStatus)) {
        return {
            visible: true,
            disabled: true,
            tooltip: "Siparişi olan kuryenin durumu güncellenemez!"
        };
    }
    return {visible: true, disabled: false, tooltip: "Durum Değiştir"};
};

export const ActiveShiftsTable = ({
    rows,
    onReload,
    onCountChange,
    statusFilter,
    startTimeFilter
}) => {
    const stores = useStore();
    const [editPopoverKey, setEditPopoverKey] = useState(null);

    const [statusModalRow, setStatusModalRow] = useState(null);
    const [timeModalRow, setTimeModalRow] = useState(null);
    const [courierModalRow, setCourierModalRow] = useState(null);
    const [deleteModalRow, setDeleteModalRow] = useState(null);
    const [cancelNote, setCancelNote] = useState(null);

    const [, shiftHoursData] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolShiftStore.getShiftHours.bind(null, {}),
        dependencies: [],
        initialCall: true
    });
    const shiftHours = shiftHoursData?.data || [];
    const canUpdateCourierStatus =
        stores.meStore.checkRole("updateCourierStatus") &&
        stores.meStore.checkPermission("liveOperation.changeCourierStatus");
    const canUpdateShift =
        stores.meStore.checkRole("liveOperation.updateShift") &&
        stores.meStore.checkPermission("liveOperation.updateShift");

    const groupedRows = useMemo(() => buildShiftRows({rows, statusFilter, startTimeFilter}), [
        rows,
        statusFilter,
        startTimeFilter
    ]);

    useEffect(() => {
        onCountChange?.((rows || []).length);
    }, [rows]);

    const reload = () => {
        setStatusModalRow(null);
        setTimeModalRow(null);
        setCourierModalRow(null);
        setDeleteModalRow(null);
        setCancelNote(null);
        onReload?.();
    };

    // Modal açıkken satırın durumu polling ile değişmiş olabilir; kaydetmeden hemen önce aynı
    // kural tekrar sorulur ki butonun pasifliği atlatılarak istek atılamasın.
    const updateCourierStatus = async values => {
        const {note, status} = values;
        const availability = courierStatusChangeAvailability(
            statusModalRow,
            canUpdateCourierStatus
        );
        if (availability.disabled) {
            stores.appStore.openErrorModal({
                content: availability.tooltip || "Bu kuryenin durumu şu anda değiştirilemiyor."
            });
            return;
        }
        await stores.courierStore.updateStatus({
            note,
            status,
            courierId: statusModalRow.courierId
        });
        reload();
    };

    const handleDedicatedDelete = row => {
        stores.appStore.openConfirmModal({
            title: "Emin misiniz?",
            content: "Bu vardiyayı silmek istediğinizden emin misiniz?",
            onOk: async () => {
                await stores.shiftStore.deleteShift({_id: row.raw._id});
                reload();
            }
        });
    };

    const handlePoolDeleteConfirm = async () => {
        if (!deleteModalRow || !cancelNote) return;
        await stores.poolShiftStore.cancelCourierPoolShift({
            courierId: deleteModalRow.courierId,
            poolShiftForecastId: deleteModalRow.raw.poolShiftForecastId,
            reason: cancelNote,
            startHourId: deleteModalRow.raw.startHourId,
            endHourId: deleteModalRow.raw.endHourId
        });
        reload();
    };

    const poolShiftData = row => ({
        poolId: row.raw.poolId,
        shiftDate: row.raw.date,
        shiftName: row.raw.poolName,
        plannedTime: `${row.raw.plannedStartTime} - ${row.raw.plannedEndTime}`,
        startHourId: row.raw.startHourId,
        endHourId: row.raw.endHourId,
        plannedStartTime: row.raw.plannedStartTime,
        plannedEndTime: row.raw.plannedEndTime
    });

    const columns = [
        {
            title: "Vardiya Tipi",
            dataIndex: "shiftType",
            width: 70,
            onCell: () => ({"data-label": "Vardiya Tipi"}),
            render: (type, row) => {
                if (row._rowType === "group") {
                    return {children: <ShiftGroupCell row={row} />, props: {colSpan: 6}};
                }
                return type === "pool" && !row.isDedicatedPool ? "Havuz" : "Dedike";
            }
        },
        {
            title: "İsim",
            dataIndex: "courierName",
            width: 130,
            onCell: () => ({"data-label": "İsim"}),
            render: (value, row) =>
                row._rowType === "group" ? {props: {colSpan: 0}} : courierNameCell(value, row)
        },
        {
            title: "Planlanan",
            key: "range",
            width: 120,
            onCell: () => ({"data-label": "Planlanan"}),
            render: (_, row) => {
                if (row._rowType === "group") return {props: {colSpan: 0}};
                return (
                    <Tooltip
                        placement="topLeft"
                        overlayInnerStyle={{
                            backgroundColor: "rgba(0, 0, 0, 1)",
                            borderRadius: 8
                        }}
                        title={
                            <>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8
                                    }}
                                >
                                    <span style={{fontWeight: 300, fontSize: 12}}>Başlangıç:</span>{" "}
                                    <b> {row.startDateTimeLabel || "-"}</b>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8
                                    }}
                                >
                                    <span style={{fontWeight: 300, fontSize: 12}}>Bitiş:</span>{" "}
                                    <b> {row.endDateTimeLabel || "-"}</b>
                                </div>
                            </>
                        }
                    >
                        <span style={{whiteSpace: "nowrap"}}>
                            {row.startLabel || "-"}
                            {dayOffsetMark(row.startDayOffset)} - {row.endLabel || "-"}
                            {dayOffsetMark(row.endDayOffset)}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            // Kuryenin fiilen vardiyaya giriş/çıkış yaptığı saatler (backend: hasCourierEnteredShift
            // ile aynı status log'ları üzerinden hesaplanır). Kurye hiç girmediyse ikisi de "-".
            title: "Gerçekleşen",
            key: "actualRange",
            width: 120,
            onCell: () => ({"data-label": "Gerçekleşen"}),
            render: (_, row) => {
                if (row._rowType === "group") return {props: {colSpan: 0}};
                return (
                    <Tooltip
                        placement="topLeft"
                        overlayInnerStyle={{
                            backgroundColor: "rgba(0, 0, 0, 1)",
                            borderRadius: 8
                        }}
                        title={
                            <>
                                <div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 8
                                        }}
                                    >
                                        <span style={{fontWeight: 300, fontSize: 12}}>Giriş:</span>{" "}
                                        <b> {row.actualStartDateTimeLabel || "-"}</b>
                                    </div>
                                    {row.actualStartDiffLabel && (
                                        <div
                                            style={{
                                                textAlign: "right",
                                                fontSize: 12,
                                                color: "#d46b08",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            ({row.actualStartDiffLabel} giriş)
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 8
                                        }}
                                    >
                                        <span style={{fontWeight: 300, fontSize: 12}}>Çıkış:</span>{" "}
                                        <b> {row.actualEndDateTimeLabel || "-"}</b>
                                    </div>
                                    {row.actualEndDiffLabel && (
                                        <div
                                            style={{
                                                textAlign: "right",
                                                fontSize: 12,
                                                color: "#d46b08",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            ({row.actualEndDiffLabel} çıkış)
                                        </div>
                                    )}
                                </div>
                            </>
                        }
                    >
                        <span style={{whiteSpace: "nowrap"}}>
                            {row.actualStartLabel || "-"}
                            {dayOffsetMark(row.actualStartDayOffset)} - {row.actualEndLabel || "-"}
                            {dayOffsetMark(row.actualEndDayOffset)}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            title: "Durum",
            dataIndex: "status",
            width: 160,
            onCell: () => ({"data-label": "Durum"}),
            render: (status, row) =>
                row._rowType === "group" ? {props: {colSpan: 0}} : statusTag(status)
        },
        {
            title: "İşlemler",
            width: 90,
            key: "actions",
            align: "right",
            onCell: () => ({"data-label": "İşlemler"}),
            render: (_, row) => {
                if (row._rowType === "group") return {props: {colSpan: 0}};
                const actions = shiftActionAvailability(
                    row,
                    stores.meStore.checkRole("isAdmin"),
                    canUpdateShift
                );
                const statusChange = courierStatusChangeAvailability(row, canUpdateCourierStatus);
                const hasEditPopoverAction =
                    actions.changeTime.visible ||
                    actions.changeCourier.visible ||
                    actions.remove.visible;

                // Pasif butonun tıklamasının işlemediğinden emin ol: disabled niteliği DOM'dan
                // düşürülüp tıklama tetiklense bile handler burada durur.
                const guarded = (action, handler) => () => {
                    if (action.disabled) return;
                    setEditPopoverKey(null);
                    handler();
                };

                return (
                    <ActionCell>
                        {statusChange.visible && (
                            <ConditionalTooltip
                                condition
                                text={statusChange.tooltip}
                                child={
                                    <IconActionButton
                                        $color="#1890ff"
                                        icon={<SyncOutlined />}
                                        disabled={statusChange.disabled}
                                        onClick={() => {
                                            if (statusChange.disabled) return;
                                            setStatusModalRow(row);
                                        }}
                                        data-testid="changeCourierStatusButton"
                                    />
                                }
                            />
                        )}
                        {hasEditPopoverAction && (
                            <Popover
                                trigger="click"
                                visible={editPopoverKey === row.key}
                                onVisibleChange={visible =>
                                    setEditPopoverKey(visible ? row.key : null)
                                }
                                title={
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between"
                                        }}
                                    >
                                        Vardiya Düzenle
                                        <CloseOutlined
                                            style={{
                                                cursor: "pointer",
                                                color: "rgba(0, 0, 0, 0.45)"
                                            }}
                                            onClick={() => setEditPopoverKey(null)}
                                            data-testid="closeEditShiftPopover"
                                        />
                                    </div>
                                }
                                content={
                                    <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                                        {actions.changeTime.visible && (
                                            <CustomButton
                                                icon={
                                                    <ClockCircleOutlined
                                                        style={{color: "#d46b08"}}
                                                    />
                                                }
                                                disabled={actions.changeTime.disabled}
                                                data-testid="changeShiftTimeButton"
                                                onClick={guarded(actions.changeTime, () =>
                                                    setTimeModalRow(row)
                                                )}
                                            >
                                                Saati Değiştir
                                            </CustomButton>
                                        )}
                                        {actions.changeCourier.visible && (
                                            <CustomButton
                                                icon={
                                                    <UserSwitchOutlined
                                                        style={{color: "#1890ff"}}
                                                    />
                                                }
                                                disabled={actions.changeCourier.disabled}
                                                data-testid="changeShiftCourierButton"
                                                onClick={guarded(actions.changeCourier, () =>
                                                    setCourierModalRow(row)
                                                )}
                                            >
                                                Kuryeyi Değiştir
                                            </CustomButton>
                                        )}
                                        {actions.remove.visible && (
                                            <CustomButton
                                                icon={<DeleteOutlined style={{color: "#f5222d"}} />}
                                                disabled={actions.remove.disabled}
                                                data-testid="deleteShiftButton"
                                                onClick={guarded(actions.remove, () => {
                                                    if (row.shiftType === "pool")
                                                        setDeleteModalRow(row);
                                                    else handleDedicatedDelete(row);
                                                })}
                                            >
                                                Vardiya Sil
                                            </CustomButton>
                                        )}
                                    </div>
                                }
                            >
                                <ConditionalTooltip
                                    condition
                                    text="Vardiya Düzenle"
                                    child={
                                        <IconActionButton
                                            $color="#237804"
                                            icon={<EditOutlined />}
                                            onClick={() => setEditPopoverKey(row.key)}
                                            data-testid="editShiftButton"
                                        />
                                    }
                                />
                            </Popover>
                        )}
                    </ActionCell>
                );
            }
        }
    ];

    return (
        <>
            <TableFillArea>
                <ResponsiveDataTable
                    className="live-operation-fill-table"
                    data-testid="activeShiftsTable"
                    scroll={{x: 780}}
                    columns={columns}
                    dataSource={groupedRows}
                    rowKey="key"
                    size="small"
                    tableLayout="fixed"
                    pagination={false}
                    rowClassName={row =>
                        row._rowType === "group"
                            ? "live-operation-group-row live-operation-group-row--courier"
                            : ""
                    }
                />
            </TableFillArea>

            <CourierStatusUpdateModal
                isVisible={Boolean(statusModalRow)}
                setVisible={() => setStatusModalRow(null)}
                defaultStatus={rawStatusFromLabel(statusModalRow?.status) || ""}
                updateStatus={updateCourierStatus}
            />

            {timeModalRow?.shiftType === "pool" && (
                <TimeChangeModal
                    visible={Boolean(timeModalRow)}
                    onCancel={() => setTimeModalRow(null)}
                    courier={timeModalRow.raw.courier}
                    shiftData={poolShiftData(timeModalRow)}
                    shiftHours={shiftHours}
                    onSuccess={reload}
                />
            )}
            {courierModalRow?.shiftType === "pool" && (
                <CourierChangeModal
                    visible={Boolean(courierModalRow)}
                    onCancel={() => setCourierModalRow(null)}
                    courier={courierModalRow.raw.courier}
                    shiftData={poolShiftData(courierModalRow)}
                    shiftHours={shiftHours}
                    onSuccess={reload}
                />
            )}

            <ShiftUpdateModal
                id={timeModalRow?.shiftType === "dedicated" ? timeModalRow.raw._id : undefined}
                courierName={timeModalRow?.courierName}
                visibility={Boolean(timeModalRow?.shiftType === "dedicated")}
                date={timeModalRow ? moment(timeModalRow.raw.date) : moment()}
                startTime={timeModalRow ? moment(timeModalRow.raw.shiftStartTime) : moment()}
                endTime={timeModalRow ? moment(timeModalRow.raw.shiftEndTime) : moment()}
                onCancel={reloaded => (reloaded ? reload() : setTimeModalRow(null))}
            />
            <ShiftCourierUpdateModal
                shift={courierModalRow?.shiftType === "dedicated" ? courierModalRow.raw : null}
                visibility={Boolean(courierModalRow?.shiftType === "dedicated")}
                onCancel={reloaded => (reloaded ? reload() : setCourierModalRow(null))}
            />

            <Modal
                title="Vardiya İptali"
                visible={Boolean(deleteModalRow)}
                onOk={handlePoolDeleteConfirm}
                onCancel={() => {
                    setDeleteModalRow(null);
                    setCancelNote(null);
                }}
                okText="İptal Et"
                cancelText="Vazgeç"
                okButtonProps={{disabled: !cancelNote}}
            >
                <p>Bu vardiyayı iptal etmek istediğinizden emin misiniz?</p>
                <Select
                    style={{width: "100%", marginTop: 16}}
                    placeholder="İptal sebebini seçin"
                    value={cancelNote}
                    onChange={setCancelNote}
                    options={cancellationReasons}
                />
            </Modal>
        </>
    );
};
